import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { queryBuilder } from '@wix/sdk-runtime/query-builder';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import {
  HttpClient,
  HttpResponse,
  Search as SearchSdkType,
  NonNullablePaths,
  QuerySpec,
  Query as QuerySdkType,
  SearchSpec,
} from '@wix/sdk-types';
import * as ambassadorWixEcomV1AbandonedCheckout from './ecom-v1-abandoned-checkout-abandoned-checkouts.http.js';
// @ts-ignore
import { transformRESTImageToSDKImage } from '@wix/sdk-runtime/transformations/image';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { createQueryUtils } from '@wix/sdk-runtime/query-builder-utils';
import { createSearchUtils } from '@wix/sdk-runtime/search-builder-utils';

/** A checkout that was started but not completed. Use abandoned checkouts to track and recover potential sales through automated follow-ups. */
export interface AbandonedCheckout {
  /**
   * Abandoned checkout ID.
   * @format GUID
   */
  _id?: string;
  /** Date and time the abandoned checkout was created. */
  _createdDate?: Date | null;
  /** Date and time the abandoned checkout was updated. */
  _updatedDate?: Date | null;
  /**
   * The associated checkout ID.
   * @format GUID
   */
  checkoutId?: string | null;
  /**
   * The associated cart ID.
   * @format GUID
   */
  cartId?: string | null;
  /** Status of the abandoned checkout. */
  status?: StatusWithLiterals;
  /**
   * Language for communication with the buyer. Defaults to the site language.
   * For a site that supports multiple languages, this is the language the buyer selected.
   */
  buyerLanguage?: string | null;
  /** Buyer information. */
  buyerInfo?: V1BuyerInfo;
  /** Contact details. */
  contactDetails?: FullAddressContactDetails;
  /**
   * The store's currency.
   * @format CURRENCY
   */
  currency?: string;
  /**
   * Currency the buyer used in checkout.
   * @format CURRENCY
   */
  conversionCurrency?: string | null;
  /** Total price after discounts, gift cards, and tax. */
  totalPrice?: MultiCurrencyPrice;
  /**
   * List of all automation activities performed by [Wix Automations](https://dev.wix.com/docs/rest/business-management/automations/introduction) regarding the abandoned checkout.
   * Wix Automations updates the `activities` field for each activity in the automation flow. Only relevant if you've set up [automations in the Dashboard](https://support.wix.com/en/article/wix-automations-creating-a-new-automation). Read more about `activities` in the [introduction](https://www.wix.com/velo/reference/wix-ecom-backend/abandonedcheckout/introduction).
   * @maxSize 20
   * @readonly
   */
  activities?: Activity[];
  /** Date and time the abandoned checkout was recovered. */
  checkoutRecoveredDate?: Date | null;
  /**
   * Checkout URL.
   * @format WEB_URL
   */
  checkoutUrl?: string;
  /** Subtotal price of all line items, before discounts and before tax. */
  subtotalPrice?: MultiCurrencyPrice;
  /**
   * Line items.
   * @minSize 1
   * @maxSize 300
   * @readonly
   */
  lineItems?: LineItem[];
  /**
   * Applied discounts.
   * @maxSize 100
   * @readonly
   */
  appliedDiscounts?: AppliedDiscount[];
}

export enum ActivityType {
  /** Scheduled. */
  SCHEDULED = 'SCHEDULED',
  /** Email sent. */
  EMAIL_SENT = 'EMAIL_SENT',
  /** Email not sent. */
  EMAIL_NOT_SENT = 'EMAIL_NOT_SENT',
  /** Notification sent. */
  NOTIFICATION_SENT = 'NOTIFICATION_SENT',
  /** Task created. */
  TASK_CREATED = 'TASK_CREATED',
  /** Automation skipped. */
  AUTOMATION_SKIPPED = 'AUTOMATION_SKIPPED',
}

/** @enumType */
export type ActivityTypeWithLiterals =
  | ActivityType
  | 'SCHEDULED'
  | 'EMAIL_SENT'
  | 'EMAIL_NOT_SENT'
  | 'NOTIFICATION_SENT'
  | 'TASK_CREATED'
  | 'AUTOMATION_SKIPPED';

/** Status of the abandoned checkout. */
export enum Status {
  /** Abandoned. */
  ABANDONED = 'ABANDONED',
  /** Recovered. */
  RECOVERED = 'RECOVERED',
}

/** @enumType */
export type StatusWithLiterals = Status | 'ABANDONED' | 'RECOVERED';

/** Buyer information. */
export interface V1BuyerInfo extends V1BuyerInfoIdOneOf {
  /**
   * Visitor ID (if the site visitor is **not** a site member).
   * @format GUID
   */
  visitorId?: string;
  /**
   * Member ID (if the site visitor is a site member).
   * @format GUID
   */
  memberId?: string;
  /**
   * User ID (if the site visitor is a site owner or collaborator).
   * @format GUID
   */
  userId?: string;
  /**
   * Contact ID. For more information, see the [Contacts API](https://www.wix.com/velo/reference/wix-crm-v2/contacts).
   * @format GUID
   * @readonly
   */
  contactId?: string | null;
  /**
   * Buyer email address.
   * Max: `50`
   * @maxLength 50
   * @format EMAIL
   */
  email?: string | null;
}

/** @oneof */
export interface V1BuyerInfoIdOneOf {
  /**
   * Visitor ID (if the site visitor is **not** a site member).
   * @format GUID
   */
  visitorId?: string;
  /**
   * Member ID (if the site visitor is a site member).
   * @format GUID
   */
  memberId?: string;
  /**
   * User ID (if the site visitor is a site owner or collaborator).
   * @format GUID
   */
  userId?: string;
}

/** Full contact details for an address */
export interface FullAddressContactDetails {
  /**
   * First name.
   * Max length: `100`
   * @maxLength 100
   */
  firstName?: string | null;
  /**
   * Last name.
   * Max length: `100`
   * @maxLength 100
   */
  lastName?: string | null;
  /**
   * Phone number.
   * @format PHONE
   */
  phone?: string | null;
  /**
   * Company name.
   * Max length: `50`
   * @maxLength 1000
   */
  company?: string | null;
  /** Tax information (for Brazil only). If ID is provided, `vatId.type` must also be set, `UNSPECIFIED` is not allowed. */
  vatId?: VatId;
}

export interface VatId {
  /** Customer's tax ID. */
  _id?: string;
  /**
   * Tax type.
   *
   * Supported values:
   * + `CPF`: for individual tax payers
   * + `CNPJ`: for corporations
   */
  type?: VatTypeWithLiterals;
}

/** tax info types */
export enum VatType {
  UNSPECIFIED = 'UNSPECIFIED',
  /** CPF - for individual tax payers. */
  CPF = 'CPF',
  /** CNPJ - for corporations */
  CNPJ = 'CNPJ',
}

/** @enumType */
export type VatTypeWithLiterals = VatType | 'UNSPECIFIED' | 'CPF' | 'CNPJ';

export interface MultiCurrencyPrice {
  /**
   * Amount.
   * @decimalValue options { gte:0, lte:1000000000000000 }
   */
  amount?: string;
  /**
   * Converted amount.
   * @readonly
   * @decimalValue options { gte:0, lte:1000000000000000 }
   */
  convertedAmount?: string;
  /**
   * Amount formatted with currency symbol.
   * @readonly
   */
  formattedAmount?: string;
  /**
   * Converted amount formatted with currency symbol.
   * @readonly
   */
  formattedConvertedAmount?: string;
}

export interface Activity {
  /** Date and time the automation activity was created. */
  _createdDate?: Date | null;
  /**
   * Automation activity type.
   *
   * Supported properties: `UNKNOWN_TYPE`, `SCHEDULED`, `EMAIL_SENT`, `EMAIL_NOT_SENT`, `NOTIFICATION_SENT`, `TASK_CREATED`
   */
  type?: ActivityTypeWithLiterals;
}

export interface LineItem {
  /**
   * Line item ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Item quantity.
   * @min 1
   * @max 100000
   */
  quantity?: number;
  /**
   * Catalog and item reference. Holds IDs for the item and the catalog it came from, as well as further optional info.
   * Optional for custom line items, which don't trigger the Catalog service plugin.
   */
  catalogReference?: CatalogReference;
  /**
   * Item name.
   * + Stores - `product.name`
   * + Bookings - `service.info.name`
   * + Events - `ticket.name`
   * @readonly
   */
  productName?: ProductName;
  /**
   * Item price **after** catalog-defined discount and line item discounts.
   * @readonly
   */
  price?: MultiCurrencyPrice;
  /**
   * Item price **before** line item discounts and **after** catalog-defined discount. Defaults to `price` when not provided.
   * @readonly
   */
  priceBeforeDiscounts?: MultiCurrencyPrice;
  /**
   * Line item description lines. Used for displaying the cart, checkout and order.
   * @maxSize 300
   * @readonly
   */
  descriptionLines?: DescriptionLine[];
  /**
   * Line item image details.
   * @readonly
   */
  image?: string;
  /**
   * Physical properties of the item. When relevant, contains information such as SKU, item weight, and shippability.
   * @readonly
   */
  physicalProperties?: PhysicalProperties;
}

/** Used for grouping line items. Sent when an item is added to a cart, checkout, or order. */
export interface CatalogReference {
  /**
   * ID of the item within the catalog it belongs to.
   * @minLength 1
   * @maxLength 36
   */
  catalogItemId?: string;
  /**
   * ID of the app providing the catalog.
   *
   * You can get your app's ID from its page in the [app dashboard](https://dev.wix.com/dc3/my-apps/).
   *
   * For items from Wix catalogs, the following values always apply:
   * + Wix Stores: `"215238eb-22a5-4c36-9e7b-e7c08025e04e"`
   * + Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`
   * + Wix Restaurants: `"9a5d83fd-8570-482e-81ab-cfa88942ee60"`
   * @minLength 1
   */
  appId?: string;
  /**
   * Additional item details in `key:value` pairs.
   *
   * Use this optional field for more specificity with item selection. The values of the `options` field differ depending on which catalog is providing the items.
   *
   * For Wix Stores products, learn more about integrating with [Catalog V3](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration)
   * or [Catalog V1](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v1/catalog/e-commerce-integration), depending on [the version the site uses](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-versioning/introduction).
   */
  options?: Record<string, any> | null;
}

export interface ProductName {
  /**
   * __Required.__ Item name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).
   *
   * Min: 1 character.
   * Max: 200 characters.
   * @minLength 1
   * @maxLength 200
   */
  original?: string;
  /**
   * Item name translated into the buyer's language.
   *
   * Min: 1 character.
   * Max: 400 characters.
   * Default: Same as `original`.
   * @minLength 1
   * @maxLength 400
   */
  translated?: string | null;
}

export interface DescriptionLine
  extends DescriptionLineValueOneOf,
    DescriptionLineDescriptionLineValueOneOf {
  /** Description line plain text value. */
  plainText?: PlainTextValue;
  /** Description line color value. */
  colorInfo?: Color;
  /**
   * Description line plain text value.
   * @internal
   * @deprecated
   */
  plainTextValue?: PlainTextValue;
  /**
   * Description line color.
   * @internal
   * @deprecated
   */
  color?: string;
  /** Description line name. */
  name?: DescriptionLineName;
  /**
   * Description line type.
   * @internal
   * @deprecated
   */
  lineType?: DescriptionLineTypeWithLiterals;
}

/** @oneof */
export interface DescriptionLineValueOneOf {
  /** Description line plain text value. */
  plainText?: PlainTextValue;
  /** Description line color value. */
  colorInfo?: Color;
}

/** @oneof */
export interface DescriptionLineDescriptionLineValueOneOf {
  /**
   * Description line plain text value.
   * @internal
   * @deprecated
   */
  plainTextValue?: PlainTextValue;
  /**
   * Description line color.
   * @internal
   * @deprecated
   */
  color?: string;
}

export interface DescriptionLineName {
  /**
   * Description line name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).
   * @maxLength 100
   */
  original?: string;
  /**
   * Description line name translated into the buyer's language.
   *
   * Default: Same as `original`.
   * @maxLength 200
   */
  translated?: string | null;
}

export interface PlainTextValue {
  /**
   * Description line plain text value in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).
   * @maxLength 600
   */
  original?: string;
  /**
   * Description line plain text value translated into the buyer's language.
   *
   * Default: Same as `original`.
   * @maxLength 600
   */
  translated?: string | null;
}

export interface Color {
  /**
   * Description line color name in the site's default language as defined in the [request envelope](https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/self-hosting/supported-extensions/backend-extensions/add-self-hosted-service-plugin-extensions#request-envelope).
   * @maxLength 500
   */
  original?: string;
  /**
   * Description line color name translated into the buyer's language.
   *
   * Default: Same as `original`.
   * @maxLength 500
   */
  translated?: string | null;
  /** HEX or RGB color code for display. */
  code?: string | null;
}

export enum DescriptionLineType {
  /** Unrecognized type. */
  UNRECOGNISED = 'UNRECOGNISED',
  /** Plain text type. */
  PLAIN_TEXT = 'PLAIN_TEXT',
  /** Color type. */
  COLOR = 'COLOR',
}

/** @enumType */
export type DescriptionLineTypeWithLiterals =
  | DescriptionLineType
  | 'UNRECOGNISED'
  | 'PLAIN_TEXT'
  | 'COLOR';

export interface PhysicalProperties {
  /** Line item weight. Measurement unit matches the weight unit specified in `weightUnit` in the request. */
  weight?: number | null;
  /**
   * Stock-keeping unit. Learn more about [SKUs](https://www.wix.com/encyclopedia/definition/stock-keeping-unit-sku).
   * @maxLength 40
   */
  sku?: string | null;
  /** Whether this line item is shippable. */
  shippable?: boolean;
  /**
   * Product dimensions (length, width, height of the physical product).
   * @internal
   */
  productDimensions?: Dimensions;
  /**
   * Package dimensions (length, width, height of the shipping package).
   * @internal
   */
  packageDimensions?: Dimensions;
}

export interface Dimensions {
  /**
   * Length. Measurement unit is handled at the system level (metric/imperial).
   * @decimalValue options { gte:0, lte:999999999, maxScale:6 }
   */
  length?: string | null;
  /**
   * Width. Measurement unit is handled at the system level (metric/imperial).
   * @decimalValue options { gte:0, lte:999999999, maxScale:6 }
   */
  width?: string | null;
  /**
   * Height. Measurement unit is handled at the system level (metric/imperial).
   * @decimalValue options { gte:0, lte:999999999, maxScale:6 }
   */
  height?: string | null;
  /** Unit of measurement for dimensions (length, width, height). */
  unit?: DimensionsUnitWithLiterals;
}

export enum DimensionsUnit {
  UNKNOWN_DIMENSIONS_UNIT = 'UNKNOWN_DIMENSIONS_UNIT',
  /** Millimeters. */
  MM = 'MM',
  /** Centimeters. */
  CM = 'CM',
  /** Meters. */
  M = 'M',
  /** Inches. */
  IN = 'IN',
  /** Feet. */
  FT = 'FT',
  /** Yards. */
  YD = 'YD',
}

/** @enumType */
export type DimensionsUnitWithLiterals =
  | DimensionsUnit
  | 'UNKNOWN_DIMENSIONS_UNIT'
  | 'MM'
  | 'CM'
  | 'M'
  | 'IN'
  | 'FT'
  | 'YD';

export interface AppliedDiscount extends AppliedDiscountDiscountSourceOneOf {
  /** Coupon details. */
  coupon?: Coupon;
  /** Merchant discount. */
  merchantDiscount?: MerchantDiscount;
  /** Discount rule */
  discountRule?: DiscountRule;
  /** Discount type. */
  discountType?: DiscountTypeWithLiterals;
  /**
   * IDs of line items the discount applies to.
   * @format GUID
   * @maxSize 300
   */
  lineItemIds?: string[];
}

/** @oneof */
export interface AppliedDiscountDiscountSourceOneOf {
  /** Coupon details. */
  coupon?: Coupon;
  /** Merchant discount. */
  merchantDiscount?: MerchantDiscount;
  /** Discount rule */
  discountRule?: DiscountRule;
}

export enum DiscountType {
  GLOBAL = 'GLOBAL',
  SPECIFIC_ITEMS = 'SPECIFIC_ITEMS',
  SHIPPING = 'SHIPPING',
}

/** @enumType */
export type DiscountTypeWithLiterals =
  | DiscountType
  | 'GLOBAL'
  | 'SPECIFIC_ITEMS'
  | 'SHIPPING';

export interface Coupon {
  /**
   * Coupon ID.
   * @maxLength 50
   */
  _id?: string;
  /**
   * Coupon code.
   * @maxLength 50
   */
  code?: string;
  /** Coupon value. */
  amount?: MultiCurrencyPrice;
  /**
   * Coupon name.
   * @maxLength 60
   */
  name?: string;
}

export interface MerchantDiscount {
  /** Discount value. */
  amount?: MultiCurrencyPrice;
  /**
   * Discount Percentage. Will be calculated from items price before other discounts.
   * @min 1
   * @max 100
   */
  percentage?: number | null;
}

export interface DiscountRule {
  /**
   * Discount rule ID
   * @format GUID
   */
  _id?: string;
  /** Discount rule name */
  name?: DiscountRuleName;
  /** Discount value. */
  amount?: MultiCurrencyPrice;
}

export interface DiscountRuleName {
  /**
   * Original discount rule name (in site's default language).
   * @minLength 1
   * @maxLength 256
   */
  original?: string;
  /**
   * Translated discount rule name according to buyer language. Defaults to `original` when not provided.
   * @minLength 1
   * @maxLength 500
   */
  translated?: string | null;
}

export interface CartAbandonedEvent {
  /** @format GUID */
  cartId?: string;
  /**
   * Time the cart was created
   * @readonly
   */
  creationTime?: Date | null;
  /**
   * Time the cart was abandoned
   * @readonly
   */
  abandonTime?: Date | null;
  /** Buyer information */
  buyerInfo?: BuyerInfo;
  /** Amount of items in cart */
  itemsCount?: number;
  /** Coupon ID (if relevant) */
  couponId?: string;
  /** Subtotal of all line items in cart, not before shipping and taxes */
  totals?: Totals;
  /**
   * Checkout URL - checkout with the abandoned cart details
   * @format WEB_URL
   */
  checkoutUrl?: string;
  /**
   * checkout id for buy now flow
   * @internal
   * @readonly
   */
  checkoutId?: string | null;
}

export interface BuyerInfo {
  /**
   * Wix customer ID
   * @format GUID
   */
  _id?: string;
  /** Customer information */
  identityType?: IdentityWithLiterals;
  /**
   * Customer's email address
   * @maxLength 50
   * @format EMAIL
   */
  email?: string | null;
  /**
   * Customer's phone number
   * @format PHONE
   */
  phone?: string | null;
  /** Customer's first name */
  firstName?: string | null;
  /** Customer's last name */
  lastName?: string | null;
}

export enum Identity {
  /** Customer is the site owner */
  ADMIN = 'ADMIN',
  /** Customer is logged in */
  MEMBER = 'MEMBER',
  /** Customer is not logged in */
  VISITOR = 'VISITOR',
  /** Contact was created for the customer */
  CONTACT = 'CONTACT',
}

/** @enumType */
export type IdentityWithLiterals =
  | Identity
  | 'ADMIN'
  | 'MEMBER'
  | 'VISITOR'
  | 'CONTACT';

export interface Totals {
  /**
   * Subtotal of all line items in cart, without shipping and taxes
   * @max 999999999.99
   */
  subtotal?: number | null;
  /**
   * Total cart price
   * @max 999999999.99
   */
  total?: number | null;
  /** Formatted total cart price includes currency symbol */
  formattedTotal?: string;
}

export interface CartRecoveredEvent {
  /** @format GUID */
  cartId?: string;
  /**
   * Time the cart was recovered
   * @readonly
   */
  recoveredTime?: Date | null;
  /**
   * Time the cart was created
   * @readonly
   */
  creationTime?: Date | null;
  /**
   * Time the cart was abandoned
   * @readonly
   */
  abandonedTime?: Date | null;
  /**
   * Checkout id
   * @internal
   * @readonly
   */
  checkoutId?: string | null;
}

/** Triggered when an abandoned checkout is recovered (the customer completes the checkout). */
export interface AbandonedCheckoutRecovered {
  /** The abandoned checkout that was recovered. */
  abandonedCheckout?: AbandonedCheckout;
}

export interface GetAbandonedCheckoutRequest {
  /**
   * Abandoned checkout ID.
   * @format GUID
   */
  abandonedCheckoutId: string;
}

export interface GetAbandonedCheckoutResponse {
  /** The requested abandoned checkout. */
  abandonedCheckout?: AbandonedCheckout;
}

export interface DeleteAbandonedCheckoutRequest {
  /**
   * Id of the abandoned checkout to delete
   * @format GUID
   */
  abandonedCheckoutId: string;
}

export interface DeleteAbandonedCheckoutResponse {}

export interface BulkDeleteAbandonedCheckoutsRequest {
  /**
   * Abandoned checkout IDs to be deleted.
   * @minSize 1
   * @maxSize 100
   * @format GUID
   */
  abandonedCheckoutIds: string[];
}

export interface BulkDeleteAbandonedCheckoutsResponse {
  /**
   * Results of the bulk delete operation.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkAbandonedCheckoutResult[];
  /** Metadata about the bulk action. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface ItemMetadata {
  /**
   * Item ID. Provided only whenever possible. For example, `itemId` can't be provided when item creation has failed.
   * @format GUID
   */
  _id?: string | null;
  /** Index of the item within the request array. Allows for correlation between request and response items. */
  originalIndex?: number;
  /** Whether the requested action for this item was successful. When `false`, the `error` field is returned. */
  success?: boolean;
  /** Details about the error in case of failure. */
  error?: ApplicationError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

export interface BulkAbandonedCheckoutResult {
  /** ID of the abandoned checkout that was deleted. */
  itemMetadata?: ItemMetadata;
}

export interface BulkActionMetadata {
  /** Number of items that were successfully processed. */
  totalSuccesses?: number;
  /** Number of items that couldn't be processed. */
  totalFailures?: number;
  /** Number of failures without details because detailed failure threshold was exceeded. */
  undetailedFailures?: number;
}

export interface QueryAbandonedCheckoutsRequest {
  /** Query options. */
  query: QueryV2;
}

export interface QueryV2 extends QueryV2PagingMethodOneOf {
  /** Paging options to limit and offset the number of items. */
  paging?: Paging;
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object.
   *
   * Learn more about [filtering](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object.
   *
   * Learn more about [sorting](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#sorting).
   */
  sort?: Sorting[];
  /** Array of projected fields. A list of specific field names to return. If `fieldsets` are also specified, the union of `fieldsets` and `fields` is returned. */
  fields?: string[];
  /** Array of named, predefined sets of projected fields. A array of predefined named sets of fields to be returned. Specifying multiple `fieldsets` will return the union of fields from all sets. If `fields` are also specified, the union of `fieldsets` and `fields` is returned. */
  fieldsets?: string[];
}

/** @oneof */
export interface QueryV2PagingMethodOneOf {
  /** Paging options to limit and offset the number of items. */
  paging?: Paging;
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
  /**
   * When `field_name` is a property of repeated field that is marked as `MATCH_ITEMS` and sort should be done by
   * a specific element from a collection, filter can/should be provided to ensure correct sort value is picked.
   *
   * If multiple filters are provided, they are combined with AND operator.
   *
   * Example:
   * Given we have document like {"id": "1", "nestedField": [{"price": 10, "region": "EU"}, {"price": 20, "region": "US"}]}
   * and `nestedField` is marked as `MATCH_ITEMS`, to ensure that sorting is done by correct region, filter should be
   * { fieldName: "nestedField.price", "select_items_by": [{"nestedField.region": "US"}] }
   * @internal
   * @maxSize 10
   */
  selectItemsBy?: Record<string, any>[] | null;
  /**
   * Origin point for geo-distance sorting on a GEO field
   * results are ordered by distance from this point (ASC = nearest first, DESC = farthest first).
   */
  origin?: AddressLocation;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface AddressLocation {
  /** Address latitude. */
  latitude?: number | null;
  /** Address longitude. */
  longitude?: number | null;
}

export interface Paging {
  /** Number of items to load. */
  limit?: number | null;
  /** Number of items to skip in the current sort order. */
  offset?: number | null;
}

export interface CursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface QueryAbandonedCheckoutsResponse {
  /**
   * __Deprecated.__ Use `abandonedCheckouts` instead.
   * @deprecated
   */
  results?: AbandonedCheckout[];
  /** List of abandoned checkouts. */
  abandonedCheckouts?: AbandonedCheckout[];
  /** Details on the paged set of results returned. */
  metadata?: PagingMetadataV2;
}

export interface PagingMetadataV2 {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Offset that was requested. */
  offset?: number | null;
  /** Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set. */
  total?: number | null;
  /** Flag that indicates the server failed to calculate the `total` field. */
  tooManyToCount?: boolean | null;
  /** Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used. */
  cursors?: Cursors;
  /**
   * Indicates if there are more results after the current page.
   * If `true`, another page of results can be retrieved.
   * If `false`, this is the last page.
   * @internal
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface SearchAbandonedCheckoutsRequest {
  /** Search options. */
  search?: Search;
}

export interface Search extends SearchPagingMethodOneOf {
  /** Paging options to limit and offset the number of items. */
  paging?: CommonPaging;
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not filter or sort. */
  cursorPaging?: CommonCursorPaging;
  /**
   * Filter object.
   *
   * Learn more about [filtering](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object.
   *
   * Learn more about [sorting](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#sorting).
   * @maxSize 4
   */
  sort?: CommonSorting[];
  /** Free text to match in searchable fields. */
  search?: SearchDetails;
}

/** @oneof */
export interface SearchPagingMethodOneOf {
  /** Paging options to limit and offset the number of items. */
  paging?: CommonPaging;
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not filter or sort. */
  cursorPaging?: CommonCursorPaging;
}

export interface CommonSorting {
  /**
   * Name of the field to sort by.
   * @maxLength 40
   */
  fieldName?: string;
  /** Sort order. */
  order?: CommonSortOrderWithLiterals;
}

export enum CommonSortOrder {
  /** Ascending order. */
  ASC = 'ASC',
  /** Descending order. */
  DESC = 'DESC',
}

/** @enumType */
export type CommonSortOrderWithLiterals = CommonSortOrder | 'ASC' | 'DESC';

export interface SearchDetails {
  /** Search mode. Defines the search logic for combining multiple terms in the `expression`. */
  mode?: ModeWithLiterals;
  /**
   * Search term or expression.
   * @maxLength 100
   */
  expression?: string | null;
  /**
   * Fields to search in. If the array is empty, all searchable fields are searched. Use dot notation to specify a JSON path. For example, `order.address.streetName`.
   * @maxLength 100
   * @maxSize 20
   */
  fields?: string[];
  /** Whether to enable the search function to use an algorithm to automatically find results that are close to the search expression, such as typos and declensions. */
  fuzzy?: boolean;
}

export enum Mode {
  /** At least one of the search terms must be present. */
  OR = 'OR',
  /** All search terms must be present. */
  AND = 'AND',
}

/** @enumType */
export type ModeWithLiterals = Mode | 'OR' | 'AND';

export interface CommonPaging {
  /**
   * Number of items to load.
   * @max 100
   */
  limit?: number | null;
  /** Number of items to skip in the current sort order. */
  offset?: number | null;
}

export interface CommonCursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   */
  cursor?: string | null;
}

export interface SearchAbandonedCheckoutsResponse {
  /** List of abandoned checkouts. */
  abandonedCheckouts?: AbandonedCheckout[];
  /** Paging metadata. Contains cursor which can be used in next query. */
  pagingMetadata?: CommonPagingMetadataV2;
}

export interface CommonPagingMetadataV2 {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Offset that was requested. */
  offset?: number | null;
  /** Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set. */
  total?: number | null;
  /** Flag that indicates the server failed to calculate the `total` field. */
  tooManyToCount?: boolean | null;
  /** Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used. */
  cursors?: CommonCursors;
  /**
   * Indicates if there are more results after the current page.
   * If `true`, another page of results can be retrieved.
   * If `false`, this is the last page.
   * @internal
   */
  hasNext?: boolean | null;
}

export interface CommonCursors {
  /** Cursor string pointing to the next page in the list of results. */
  next?: string | null;
  /** Cursor pointing to the previous page in the list of results. */
  prev?: string | null;
}

export interface AddAbandonedCheckoutActivityRequest {
  /**
   * Id of the abandoned checkout to update
   * @format GUID
   */
  abandonedCheckoutId: string | null;
  /** The type of the activity to add */
  activityType?: ActivityTypeWithLiterals;
}

export interface AddAbandonedCheckoutActivityResponse {
  /** The updated abandoned checkout with the added activity */
  abandonedCheckout?: AbandonedCheckout;
}

export interface GetAbandonedCheckoutAnalyticsRequest {
  /**
   * First day to include in the aggregation, inclusive, as a `YYYY-MM-DD` calendar date
   * (e.g. `2022-06-01`) interpreted in the site's time zone.
   * @format LOCAL_DATE
   * @minLength 10
   */
  startDate: string;
  /**
   * Last day to include in the aggregation, inclusive, as a `YYYY-MM-DD` calendar date
   * (e.g. `2022-07-06`) interpreted in the site's time zone. Must be on or after `start_date`.
   * @format LOCAL_DATE
   * @minLength 10
   */
  endDate: string;
  /**
   * Metrics to compute. Each selected metric populates its matching response field, leaving the
   * rest unset. Leave empty to compute every metric.
   *
   * Metrics are computed only over the checkouts left after `statuses` is applied, so a
   * recovered-only metric (`RECOVERED_VALUE`, `RECOVERED_CHECKOUT_COUNT`) combined with
   * `statuses = [ABANDONED]` returns `0`, and a recoverable-only metric
   * (`RECOVERABLE_VALUE`, `RECOVERABLE_CHECKOUT_COUNT`) combined with `statuses = [RECOVERED]`
   * returns `0`. Leave `statuses` empty to compute each metric over its full population.
   * @maxSize 6
   */
  metrics?: MetricWithLiterals[];
  /**
   * Restricts the aggregation to abandoned checkouts currently in these recovery states
   * (`ABANDONED`, `RECOVERED`). Leave empty to include checkouts regardless of status.
   *
   * This filter scopes the population that every requested metric is computed over. Pairing it
   * with a metric for the opposite state yields `0` (e.g. `statuses = [ABANDONED]` with
   * `RECOVERED_VALUE`); leave empty unless you intend to narrow all metrics to these states.
   * @maxSize 2
   */
  statuses?: StatusWithLiterals[];
}

/**
 * A metric that can be requested for computation. Each value maps to the identically named field
 * in `GetAbandonedCheckoutAnalyticsResponse`; request a subset via `metrics` to compute only those.
 */
export enum Metric {
  /** Total value of recoverable checkouts that were abandoned but not yet recovered. */
  RECOVERABLE_VALUE = 'RECOVERABLE_VALUE',
  /** Count of recoverable checkouts in the range that were abandoned but not yet recovered. */
  RECOVERABLE_CHECKOUT_COUNT = 'RECOVERABLE_CHECKOUT_COUNT',
  /**
   * Average value of a recoverable checkout that was not yet recovered: total value of recoverable checkouts
   * that were not yet recovered, divided by the count of recoverable checkouts that were not yet recovered.
   */
  AVERAGE_RECOVERABLE_CHECKOUT_VALUE = 'AVERAGE_RECOVERABLE_CHECKOUT_VALUE',
  /** Total value of the checkouts that were later recovered. */
  RECOVERED_VALUE = 'RECOVERED_VALUE',
  /** Count of checkouts that were later recovered. */
  RECOVERED_CHECKOUT_COUNT = 'RECOVERED_CHECKOUT_COUNT',
  /** Share of all recoverable checkouts that were recovered: recovered_checkout_count / (recoverable_checkout_count + recovered_checkout_count). */
  RECOVERY_RATE = 'RECOVERY_RATE',
}

/** @enumType */
export type MetricWithLiterals =
  | Metric
  | 'RECOVERABLE_VALUE'
  | 'RECOVERABLE_CHECKOUT_COUNT'
  | 'AVERAGE_RECOVERABLE_CHECKOUT_VALUE'
  | 'RECOVERED_VALUE'
  | 'RECOVERED_CHECKOUT_COUNT'
  | 'RECOVERY_RATE';

/**
 * Site-level aggregate over the requested date range. Each field is populated only when its metric was
 * requested in `metrics` (or `metrics` was left empty); metrics that were not requested are left unset.
 * Monetary amounts are decimal strings (e.g. `"1250.90"`) denominated in `currency`.
 */
export interface GetAbandonedCheckoutAnalyticsResponse {
  /**
   * ISO-4217 code (e.g. `USD`) that every monetary amount in this response is expressed in; the site's currency.
   * @format CURRENCY
   */
  currency?: string;
  /**
   * Total value of recoverable checkouts that were abandoned but not yet recovered.
   * @decimalValue options { gte:0, maxScale:2 }
   */
  recoverableValue?: string | null;
  /** Count of recoverable checkouts that were not recovered. */
  recoverableCheckoutCount?: number | null;
  /**
   * Average value of a recoverable checkout that was not yet recovered: total value of recoverable checkouts
   * that were not yet recovered, divided by the count of recoverable checkouts that were not yet recovered.
   * @decimalValue options { gte:0, maxScale:2 }
   */
  averageRecoverableCheckoutValue?: string | null;
  /**
   * Total value of the checkouts that were later recovered.
   * @decimalValue options { gte:0, maxScale:2 }
   */
  recoveredValue?: string | null;
  /** Count of checkouts that were later recovered. */
  recoveredCheckoutCount?: number | null;
  /** Share of recoverable checkouts that were recovered, from `0` (none) to `1` (all). */
  recoveryRate?: number | null;
}

export interface Task {
  key?: TaskKey;
  executeAt?: Date | null;
  payload?: string | null;
}

export interface TaskKey {
  appId?: string;
  instanceId?: string;
  subjectId?: string | null;
}

export interface TaskAction extends TaskActionActionOneOf {
  complete?: Complete;
  cancel?: Cancel;
  reschedule?: Reschedule;
}

/** @oneof */
export interface TaskActionActionOneOf {
  complete?: Complete;
  cancel?: Cancel;
  reschedule?: Reschedule;
}

export interface Complete {}

export interface Cancel {}

export interface Reschedule {
  executeAt?: Date | null;
  payload?: string | null;
}

export interface RedirectToCheckoutRequest {
  /**
   * ID of the abandoned checkout.
   * @format GUID
   */
  abandonedCheckoutId: string;
  /**
   * Metasite ID of the site associated with the abandoned checkout.
   * @format GUID
   */
  metasiteId: string;
}

export interface RawHttpResponse {
  body?: Uint8Array;
  statusCode?: number | null;
  headers?: HeadersEntry[];
}

export interface HeadersEntry {
  key?: string;
  value?: string;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface Empty {}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

export interface BaseEventMetadata {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface EventMetadata extends BaseEventMetadata {
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
  accountInfo?: AccountInfoMetadata;
}

export interface AccountInfoMetadata {
  /** ID of the Wix account associated with the event */
  accountId: string;
  /** ID of the Wix site associated with the event. Only included when the event is tied to a specific site. */
  siteId?: string;
  /** ID of the parent Wix account. Only included when 'accountId' belongs to a child account. */
  parentAccountId?: string;
}

export interface AbandonedCheckoutCreatedEnvelope {
  entity: AbandonedCheckout;
  metadata: EventMetadata;
}

/**
 * Triggered when an abandoned checkout is created.
 * @permissionScope Manage Stores - all permissions
 * @permissionScopeId SCOPE.DC-STORES-MEGA.MANAGE-STORES
 * @permissionScope Manage Stores
 * @permissionScopeId SCOPE.STORES.MANAGE-STORES
 * @permissionScope Read eCommerce - all read permissions
 * @permissionScopeId SCOPE.DC-ECOM-MEGA.READ-ECOM
 * @permissionScope Read Orders
 * @permissionScopeId SCOPE.DC-STORES.READ-ORDERS
 * @permissionScope Read Stores - all read permissions
 * @permissionScopeId SCOPE.DC-STORES-MEGA.READ-STORES
 * @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionScope Manage eCommerce - all permissions
 * @permissionScopeId SCOPE.DC-ECOM-MEGA.MANAGE-ECOM
 * @permissionScope Manage Orders
 * @permissionScopeId SCOPE.DC-STORES.MANAGE-ORDERS
 * @permissionId ECOM.ABANDONED_CHECKOUT_READ
 * @webhook
 * @eventType wix.ecom.v1.abandoned_checkout_created
 * @slug created
 */
export declare function onAbandonedCheckoutCreated(
  handler: (event: AbandonedCheckoutCreatedEnvelope) => void | Promise<void>
): void;

export interface AbandonedCheckoutDeletedEnvelope {
  entity: AbandonedCheckout;
  metadata: EventMetadata;
}

/**
 * Triggerred when an abandoned checkout is deleted.
 * @permissionScope Manage Stores - all permissions
 * @permissionScopeId SCOPE.DC-STORES-MEGA.MANAGE-STORES
 * @permissionScope Manage Stores
 * @permissionScopeId SCOPE.STORES.MANAGE-STORES
 * @permissionScope Read eCommerce - all read permissions
 * @permissionScopeId SCOPE.DC-ECOM-MEGA.READ-ECOM
 * @permissionScope Read Orders
 * @permissionScopeId SCOPE.DC-STORES.READ-ORDERS
 * @permissionScope Read Stores - all read permissions
 * @permissionScopeId SCOPE.DC-STORES-MEGA.READ-STORES
 * @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionScope Manage eCommerce - all permissions
 * @permissionScopeId SCOPE.DC-ECOM-MEGA.MANAGE-ECOM
 * @permissionScope Manage Orders
 * @permissionScopeId SCOPE.DC-STORES.MANAGE-ORDERS
 * @permissionId ECOM.ABANDONED_CHECKOUT_READ
 * @webhook
 * @eventType wix.ecom.v1.abandoned_checkout_deleted
 * @slug deleted
 */
export declare function onAbandonedCheckoutDeleted(
  handler: (event: AbandonedCheckoutDeletedEnvelope) => void | Promise<void>
): void;

export interface AbandonedCheckoutRecoveredEnvelope {
  data: AbandonedCheckoutRecovered;
  metadata: EventMetadata;
}

/**
 * Triggered when an abandoned checkout is recovered.
 * @permissionScope Manage Stores - all permissions
 * @permissionScopeId SCOPE.DC-STORES-MEGA.MANAGE-STORES
 * @permissionScope Manage Stores
 * @permissionScopeId SCOPE.STORES.MANAGE-STORES
 * @permissionScope Read eCommerce - all read permissions
 * @permissionScopeId SCOPE.DC-ECOM-MEGA.READ-ECOM
 * @permissionScope Read Orders
 * @permissionScopeId SCOPE.DC-STORES.READ-ORDERS
 * @permissionScope Read Stores - all read permissions
 * @permissionScopeId SCOPE.DC-STORES-MEGA.READ-STORES
 * @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionScope Manage eCommerce - all permissions
 * @permissionScopeId SCOPE.DC-ECOM-MEGA.MANAGE-ECOM
 * @permissionScope Manage Orders
 * @permissionScopeId SCOPE.DC-STORES.MANAGE-ORDERS
 * @permissionId ECOM.ABANDONED_CHECKOUT_READ
 * @webhook
 * @eventType wix.ecom.v1.abandoned_checkout_recovered
 * @slug recovered
 */
export declare function onAbandonedCheckoutRecovered(
  handler: (event: AbandonedCheckoutRecoveredEnvelope) => void | Promise<void>
): void;

export interface AbandonedCheckoutUpdatedEnvelope {
  entity: AbandonedCheckout;
  metadata: EventMetadata;
}

/**
 * Triggered when an abandoned checkout is updated.
 * @permissionScope Manage Stores - all permissions
 * @permissionScopeId SCOPE.DC-STORES-MEGA.MANAGE-STORES
 * @permissionScope Manage Stores
 * @permissionScopeId SCOPE.STORES.MANAGE-STORES
 * @permissionScope Read eCommerce - all read permissions
 * @permissionScopeId SCOPE.DC-ECOM-MEGA.READ-ECOM
 * @permissionScope Read Orders
 * @permissionScopeId SCOPE.DC-STORES.READ-ORDERS
 * @permissionScope Read Stores - all read permissions
 * @permissionScopeId SCOPE.DC-STORES-MEGA.READ-STORES
 * @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionScope Manage eCommerce - all permissions
 * @permissionScopeId SCOPE.DC-ECOM-MEGA.MANAGE-ECOM
 * @permissionScope Manage Orders
 * @permissionScopeId SCOPE.DC-STORES.MANAGE-ORDERS
 * @permissionId ECOM.ABANDONED_CHECKOUT_READ
 * @webhook
 * @eventType wix.ecom.v1.abandoned_checkout_updated
 * @slug updated
 */
export declare function onAbandonedCheckoutUpdated(
  handler: (event: AbandonedCheckoutUpdatedEnvelope) => void | Promise<void>
): void;

/**
 * Retrieves an abandoned checkout.
 * @param abandonedCheckoutId - Abandoned checkout ID.
 * @public
 * @requiredField abandonedCheckoutId
 * @permissionId ECOM.ABANDONED_CHECKOUT_READ
 * @applicableIdentity APP
 * @returns The requested abandoned checkout.
 * @fqn wix.ecom.abandoned_checkout.api.v1.AbandonedCheckoutService.GetAbandonedCheckout
 */
export async function getAbandonedCheckout(
  abandonedCheckoutId: string
): Promise<
  NonNullablePaths<
    AbandonedCheckout,
    | `_id`
    | `status`
    | `buyerInfo.visitorId`
    | `buyerInfo.memberId`
    | `buyerInfo.userId`
    | `contactDetails.vatId._id`
    | `contactDetails.vatId.type`
    | `currency`
    | `totalPrice.amount`
    | `totalPrice.convertedAmount`
    | `totalPrice.formattedAmount`
    | `totalPrice.formattedConvertedAmount`
    | `activities`
    | `activities.${number}.type`
    | `checkoutUrl`
    | `lineItems`
    | `lineItems.${number}.quantity`
    | `lineItems.${number}.catalogReference.catalogItemId`
    | `lineItems.${number}.catalogReference.appId`
    | `lineItems.${number}.productName.original`
    | `lineItems.${number}.physicalProperties.shippable`
    | `appliedDiscounts`
    | `appliedDiscounts.${number}.coupon._id`
    | `appliedDiscounts.${number}.coupon.code`
    | `appliedDiscounts.${number}.coupon.name`
    | `appliedDiscounts.${number}.discountRule._id`
    | `appliedDiscounts.${number}.discountRule.name.original`
    | `appliedDiscounts.${number}.discountType`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    abandonedCheckoutId: abandonedCheckoutId,
  });

  const reqOpts =
    ambassadorWixEcomV1AbandonedCheckout.getAbandonedCheckout(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'abandonedCheckout.lineItems.image' }],
        },
      ])
    )?.abandonedCheckout!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { abandonedCheckoutId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['abandonedCheckoutId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Deletes an abandoned checkout.
 * @param abandonedCheckoutId - Id of the abandoned checkout to delete
 * @public
 * @requiredField abandonedCheckoutId
 * @permissionId ECOM.ABANDONED_CHECKOUT_DELETE
 * @applicableIdentity APP
 * @fqn wix.ecom.abandoned_checkout.api.v1.AbandonedCheckoutService.DeleteAbandonedCheckout
 */
export async function deleteAbandonedCheckout(
  abandonedCheckoutId: string
): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    abandonedCheckoutId: abandonedCheckoutId,
  });

  const reqOpts =
    ambassadorWixEcomV1AbandonedCheckout.deleteAbandonedCheckout(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { abandonedCheckoutId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['abandonedCheckoutId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Deletes multiple abandoned checkouts.
 * @param abandonedCheckoutIds - Abandoned checkout IDs to be deleted.
 * @public
 * @requiredField abandonedCheckoutIds
 * @permissionId ECOM.ABANDONED_CHECKOUT_DELETE
 * @applicableIdentity APP
 * @fqn wix.ecom.abandoned_checkout.api.v1.AbandonedCheckoutService.BulkDeleteAbandonedCheckouts
 */
export async function bulkDeleteAbandonedCheckouts(
  abandonedCheckoutIds: string[]
): Promise<
  NonNullablePaths<
    BulkDeleteAbandonedCheckoutsResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    abandonedCheckoutIds: abandonedCheckoutIds,
  });

  const reqOpts =
    ambassadorWixEcomV1AbandonedCheckout.bulkDeleteAbandonedCheckouts(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { abandonedCheckoutIds: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['abandonedCheckoutIds']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Creates a query to retrieve a list of abandoned checkouts.
 *
 * The `queryAbandonedCheckouts()` function builds a query to retrieve a list of abandoned checkouts and returns a `ResultsQueryBuilder` object.
 *
 * The returned object contains the query definition, which is typically used to run the query using the `find()` function.
 *
 * You can refine the query by chaining `ResultsQueryBuilder` functions onto the query. `ResultsQueryBuilder` functions enable you to sort, filter, and control the results `queryAbandonedCheckouts()` returns.
 * @public
 * @permissionId ECOM.ABANDONED_CHECKOUT_READ
 * @applicableIdentity APP
 * @fqn wix.ecom.abandoned_checkout.api.v1.AbandonedCheckoutService.QueryAbandonedCheckouts
 */
export function queryAbandonedCheckouts(): AbandonedCheckoutsQueryBuilder {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  return queryBuilder<
    AbandonedCheckout,
    'CURSOR',
    QueryAbandonedCheckoutsRequest,
    QueryAbandonedCheckoutsResponse
  >({
    func: async (payload: QueryAbandonedCheckoutsRequest) => {
      const reqOpts =
        ambassadorWixEcomV1AbandonedCheckout.queryAbandonedCheckouts(payload);

      sideEffects?.onSiteCall?.();
      try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
        return result;
      } catch (err) {
        sideEffects?.onError?.(err);
        throw err;
      }
    },
    requestTransformer: (query: QueryAbandonedCheckoutsRequest['query']) => {
      const args = [query, {}] as [QueryAbandonedCheckoutsRequest['query'], {}];
      return renameKeysFromSDKRequestToRESTRequest({
        ...args?.[1],
        query: args?.[0],
      });
    },
    responseTransformer: ({
      data,
    }: HttpResponse<QueryAbandonedCheckoutsResponse>) => {
      const transformedData = renameKeysFromRESTResponseToSDKResponse(
        transformPaths(data, [
          {
            transformFn: transformRESTImageToSDKImage,
            paths: [
              { path: 'results.lineItems.image' },
              { path: 'abandonedCheckouts.lineItems.image' },
            ],
          },
        ])
      );

      return {
        items: transformedData?.abandonedCheckouts,
        pagingMetadata: transformedData?.metadata,
      };
    },
    errorTransformer: (err: unknown) => {
      const transformedError = sdkTransformError(err, {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      });

      throw transformedError;
    },
    pagingMethod: 'CURSOR',
    transformationPaths: {},
  });
}

interface QueryCursorResult {
  cursors: CommonCursors;
  hasNext: () => boolean;
  hasPrev: () => boolean;
  length: number;
  pageSize: number;
}

export interface AbandonedCheckoutsQueryResult extends QueryCursorResult {
  items: AbandonedCheckout[];
  query: AbandonedCheckoutsQueryBuilder;
  next: () => Promise<AbandonedCheckoutsQueryResult>;
  prev: () => Promise<AbandonedCheckoutsQueryResult>;
}

export interface AbandonedCheckoutsQueryBuilder {
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  eq: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'status'
      | 'buyerInfo.visitorId'
      | 'buyerInfo.memberId'
      | 'buyerInfo.userId'
      | 'buyerInfo.contactId'
      | 'buyerInfo.email',
    value: any
  ) => AbandonedCheckoutsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ne: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'status'
      | 'buyerInfo.visitorId'
      | 'buyerInfo.memberId'
      | 'buyerInfo.userId'
      | 'buyerInfo.contactId'
      | 'buyerInfo.email',
    value: any
  ) => AbandonedCheckoutsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ge: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'buyerInfo.visitorId'
      | 'buyerInfo.memberId'
      | 'buyerInfo.userId'
      | 'buyerInfo.contactId'
      | 'buyerInfo.email',
    value: any
  ) => AbandonedCheckoutsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  gt: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'buyerInfo.visitorId'
      | 'buyerInfo.memberId'
      | 'buyerInfo.userId'
      | 'buyerInfo.contactId'
      | 'buyerInfo.email',
    value: any
  ) => AbandonedCheckoutsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  le: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'buyerInfo.visitorId'
      | 'buyerInfo.memberId'
      | 'buyerInfo.userId'
      | 'buyerInfo.contactId'
      | 'buyerInfo.email',
    value: any
  ) => AbandonedCheckoutsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  lt: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'buyerInfo.visitorId'
      | 'buyerInfo.memberId'
      | 'buyerInfo.userId'
      | 'buyerInfo.contactId'
      | 'buyerInfo.email',
    value: any
  ) => AbandonedCheckoutsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `string`.
   * @param string - String to compare against. Case-insensitive.
   */
  startsWith: (
    propertyName:
      | '_id'
      | 'buyerInfo.visitorId'
      | 'buyerInfo.memberId'
      | 'buyerInfo.userId'
      | 'buyerInfo.contactId'
      | 'buyerInfo.email',
    value: string
  ) => AbandonedCheckoutsQueryBuilder;
  in: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'status'
      | 'buyerInfo.visitorId'
      | 'buyerInfo.memberId'
      | 'buyerInfo.userId'
      | 'buyerInfo.contactId'
      | 'buyerInfo.email',
    value: any
  ) => AbandonedCheckoutsQueryBuilder;
  exists: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'status'
      | 'buyerInfo.visitorId'
      | 'buyerInfo.memberId'
      | 'buyerInfo.userId'
      | 'buyerInfo.contactId'
      | 'buyerInfo.email',
    value: boolean
  ) => AbandonedCheckoutsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments. */
  ascending: (
    ...propertyNames: Array<
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'status'
      | 'buyerInfo.visitorId'
      | 'buyerInfo.memberId'
      | 'buyerInfo.userId'
      | 'buyerInfo.contactId'
      | 'buyerInfo.email'
    >
  ) => AbandonedCheckoutsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments. */
  descending: (
    ...propertyNames: Array<
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'status'
      | 'buyerInfo.visitorId'
      | 'buyerInfo.memberId'
      | 'buyerInfo.userId'
      | 'buyerInfo.contactId'
      | 'buyerInfo.email'
    >
  ) => AbandonedCheckoutsQueryBuilder;
  /** @param limit - Number of items to return, which is also the `pageSize` of the results object. */
  limit: (limit: number) => AbandonedCheckoutsQueryBuilder;
  /** @param cursor - A pointer to specific record */
  skipTo: (cursor: string) => AbandonedCheckoutsQueryBuilder;
  find: () => Promise<AbandonedCheckoutsQueryResult>;
}

/**
 * @hidden
 * @fqn wix.ecom.abandoned_checkout.api.v1.AbandonedCheckoutService.QueryAbandonedCheckouts
 * @requiredField query
 */
export async function typedQueryAbandonedCheckouts(
  query: AbandonedCheckoutQuery
): Promise<
  NonNullablePaths<
    QueryAbandonedCheckoutsResponse,
    | `results`
    | `results.${number}._id`
    | `results.${number}.status`
    | `results.${number}.buyerInfo.visitorId`
    | `results.${number}.buyerInfo.memberId`
    | `results.${number}.buyerInfo.userId`
    | `results.${number}.contactDetails.vatId._id`
    | `results.${number}.contactDetails.vatId.type`
    | `results.${number}.currency`
    | `results.${number}.totalPrice.amount`
    | `results.${number}.totalPrice.convertedAmount`
    | `results.${number}.totalPrice.formattedAmount`
    | `results.${number}.totalPrice.formattedConvertedAmount`
    | `results.${number}.checkoutUrl`
    | `abandonedCheckouts`
    | `abandonedCheckouts.${number}._id`
    | `abandonedCheckouts.${number}.status`
    | `abandonedCheckouts.${number}.buyerInfo.visitorId`
    | `abandonedCheckouts.${number}.buyerInfo.memberId`
    | `abandonedCheckouts.${number}.buyerInfo.userId`
    | `abandonedCheckouts.${number}.contactDetails.vatId._id`
    | `abandonedCheckouts.${number}.contactDetails.vatId.type`
    | `abandonedCheckouts.${number}.currency`
    | `abandonedCheckouts.${number}.totalPrice.amount`
    | `abandonedCheckouts.${number}.totalPrice.convertedAmount`
    | `abandonedCheckouts.${number}.totalPrice.formattedAmount`
    | `abandonedCheckouts.${number}.totalPrice.formattedConvertedAmount`
    | `abandonedCheckouts.${number}.checkoutUrl`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ query: query });

  const reqOpts =
    ambassadorWixEcomV1AbandonedCheckout.queryAbandonedCheckouts(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'results.lineItems.image' },
            { path: 'abandonedCheckouts.lineItems.image' },
          ],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['query']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface AbandonedCheckoutQuerySpec extends QuerySpec {
  paging: 'cursor';
  wql: [
    {
      fields: [
        '_createdDate',
        '_id',
        '_updatedDate',
        'buyerInfo.contactId',
        'buyerInfo.email',
        'buyerInfo.memberId',
        'buyerInfo.userId',
        'buyerInfo.visitorId',
        'status'
      ];
      operators: '*';
      sort: 'BOTH';
    }
  ];
}

export type CommonQueryWithEntityContext = QuerySdkType<
  AbandonedCheckout,
  AbandonedCheckoutQuerySpec
>;
export type AbandonedCheckoutQuery = {
  /** 
  Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`.  
  */
  cursorPaging?: {
    /** 
  Maximum number of items to return in the results. 
  @max: 100 
  */
    limit?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['limit']
      | null;
    /** 
  Pointer to the next or previous page in the list of results.

  Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
  Not relevant for the first request. 
  @maxLength: 16000 
  */
    cursor?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['cursor']
      | null;
  };
  /** 
  Filter object.

  Learn more about [filtering](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).  
  */
  filter?: CommonQueryWithEntityContext['filter'] | null;
  /** 
  Sort object.

  Learn more about [sorting](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#sorting).  
  */
  sort?: {
    /** 
  Name of the field to sort by. 
  @maxLength: 512 
  */
    fieldName?: NonNullable<
      CommonQueryWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Sort order.  
  */
    order?: NonNullable<CommonQueryWithEntityContext['sort']>[number]['order'];
    /** 
  When `field_name` is a property of repeated field that is marked as `MATCH_ITEMS` and sort should be done by
  a specific element from a collection, filter can/should be provided to ensure correct sort value is picked.

  If multiple filters are provided, they are combined with AND operator.

  Example:
  Given we have document like {"id": "1", "nestedField": [{"price": 10, "region": "EU"}, {"price": 20, "region": "US"}]}
  and `nestedField` is marked as `MATCH_ITEMS`, to ensure that sorting is done by correct region, filter should be
  { fieldName: "nestedField.price", "select_items_by": [{"nestedField.region": "US"}] } 
  @internal: undefined,
  @maxSize: 10 
  */
    selectItemsBy?:
      | NonNullable<
          CommonQueryWithEntityContext['sort']
        >[number]['selectItemsBy']
      | null;
    /** 
  Origin point for geo-distance sorting on a GEO field
  results are ordered by distance from this point (ASC = nearest first, DESC = farthest first).  
  */
    origin?: NonNullable<
      CommonQueryWithEntityContext['sort']
    >[number]['origin'];
  }[];
};

export const utils = {
  query: /*#__PURE__*/ createQueryUtils<
    AbandonedCheckout,
    AbandonedCheckoutQuerySpec,
    AbandonedCheckoutQuery
  >(),
  search: /*#__PURE__*/ createSearchUtils<
    AbandonedCheckout,
    AbandonedCheckoutSearchSpec,
    AbandonedCheckoutSearch
  >(),
};

/**
 * Retrieves a list of abandoned checkouts, given the provided paging, filtering, search details and sorting. Up to 100 abandoned checkouts can be returned per request.
 * @param search - Search options.
 * @public
 * @requiredField search
 * @permissionId ECOM.ABANDONED_CHECKOUT_READ
 * @applicableIdentity APP
 * @fqn wix.ecom.abandoned_checkout.api.v1.AbandonedCheckoutService.SearchAbandonedCheckouts
 */
export async function searchAbandonedCheckouts(
  search: AbandonedCheckoutSearch
): Promise<
  NonNullablePaths<
    SearchAbandonedCheckoutsResponse,
    | `abandonedCheckouts`
    | `abandonedCheckouts.${number}._id`
    | `abandonedCheckouts.${number}.status`
    | `abandonedCheckouts.${number}.buyerInfo.visitorId`
    | `abandonedCheckouts.${number}.buyerInfo.memberId`
    | `abandonedCheckouts.${number}.buyerInfo.userId`
    | `abandonedCheckouts.${number}.contactDetails.vatId._id`
    | `abandonedCheckouts.${number}.contactDetails.vatId.type`
    | `abandonedCheckouts.${number}.currency`
    | `abandonedCheckouts.${number}.totalPrice.amount`
    | `abandonedCheckouts.${number}.totalPrice.convertedAmount`
    | `abandonedCheckouts.${number}.totalPrice.formattedAmount`
    | `abandonedCheckouts.${number}.totalPrice.formattedConvertedAmount`
    | `abandonedCheckouts.${number}.checkoutUrl`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ search: search });

  const reqOpts =
    ambassadorWixEcomV1AbandonedCheckout.searchAbandonedCheckouts(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'abandonedCheckouts.lineItems.image' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { search: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['search']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface AbandonedCheckoutSearchSpec extends SearchSpec {
  searchable: [
    'buyerInfo.email',
    'contactDetails.firstName',
    'contactDetails.lastName'
  ];
  paging: 'cursor';
  wql: [
    {
      operators: ['$hasAll', '$hasSome'];
      fields: ['activities.type', 'appliedDiscounts.coupon.code'];
      sort: 'NONE';
    },
    {
      operators: '*';
      fields: ['contactDetails.firstName', 'contactDetails.lastName'];
      sort: 'NONE';
    },
    {
      operators: '*';
      fields: ['_createdDate', '_updatedDate'];
      sort: 'BOTH';
    },
    {
      operators: ['$eq', '$in', '$ne', '$nin'];
      fields: [
        '_id',
        'buyerInfo.contactId',
        'buyerInfo.email',
        'buyerInfo.memberId',
        'buyerInfo.userId',
        'buyerInfo.visitorId',
        'status'
      ];
      sort: 'NONE';
    }
  ];
}

export type CommonSearchWithEntityContext = SearchSdkType<
  AbandonedCheckout,
  AbandonedCheckoutSearchSpec
>;
export type AbandonedCheckoutSearch = {
  /** 
  Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not filter or sort.  
  */
  cursorPaging?: {
    /** 
  Maximum number of items to return in the results. 
  @max: 100 
  */
    limit?:
      | NonNullable<CommonSearchWithEntityContext['cursorPaging']>['limit']
      | null;
    /** 
  Pointer to the next or previous page in the list of results.

  Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
  Not relevant for the first request.  
  */
    cursor?:
      | NonNullable<CommonSearchWithEntityContext['cursorPaging']>['cursor']
      | null;
  };
  /** 
  Filter object.

  Learn more about [filtering](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).  
  */
  filter?: CommonSearchWithEntityContext['filter'] | null;
  /** 
  Sort object.

  Learn more about [sorting](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#sorting). 
  @maxSize: 4 
  */
  sort?: {
    /** 
  Name of the field to sort by. 
  @maxLength: 40 
  */
    fieldName?: NonNullable<
      CommonSearchWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Sort order.  
  */
    order?: NonNullable<CommonSearchWithEntityContext['sort']>[number]['order'];
  }[];
  /** 
  Free text to match in searchable fields.  
  */
  search?: {
    /** 
  Search mode. Defines the search logic for combining multiple terms in the `expression`.  
  */
    mode?: NonNullable<CommonSearchWithEntityContext['search']>['mode'];
    /** 
  Search term or expression. 
  @maxLength: 100 
  */
    expression?:
      | NonNullable<CommonSearchWithEntityContext['search']>['expression']
      | null;
    /** 
  Fields to search in. If the array is empty, all searchable fields are searched. Use dot notation to specify a JSON path. For example, `order.address.streetName`. 
  @maxLength: 100,
  @maxSize: 20 
  */
    fields?: NonNullable<CommonSearchWithEntityContext['search']>['fields'];
    /** 
  Whether to enable the search function to use an algorithm to automatically find results that are close to the search expression, such as typos and declensions.  
  */
    fuzzy?: NonNullable<CommonSearchWithEntityContext['search']>['fuzzy'];
  };
};

/**
 * Adds a new abandoned checkout activity.
 * @param abandonedCheckoutId - Id of the abandoned checkout to update
 * @internal
 * @documentationMaturity preview
 * @requiredField abandonedCheckoutId
 * @permissionId ECOM.ABANDONED_CHECKOUT_UPDATE
 * @applicableIdentity APP
 * @fqn wix.ecom.abandoned_checkout.api.v1.AbandonedCheckoutService.AddAbandonedCheckoutActivity
 */
export async function addAbandonedCheckoutActivity(
  abandonedCheckoutId: string,
  options?: AddAbandonedCheckoutActivityOptions
): Promise<
  NonNullablePaths<
    AddAbandonedCheckoutActivityResponse,
    | `abandonedCheckout._id`
    | `abandonedCheckout.status`
    | `abandonedCheckout.buyerInfo.visitorId`
    | `abandonedCheckout.buyerInfo.memberId`
    | `abandonedCheckout.buyerInfo.userId`
    | `abandonedCheckout.contactDetails.vatId._id`
    | `abandonedCheckout.contactDetails.vatId.type`
    | `abandonedCheckout.currency`
    | `abandonedCheckout.totalPrice.amount`
    | `abandonedCheckout.totalPrice.convertedAmount`
    | `abandonedCheckout.totalPrice.formattedAmount`
    | `abandonedCheckout.totalPrice.formattedConvertedAmount`
    | `abandonedCheckout.activities`
    | `abandonedCheckout.activities.${number}.type`
    | `abandonedCheckout.checkoutUrl`
    | `abandonedCheckout.lineItems`
    | `abandonedCheckout.lineItems.${number}.quantity`
    | `abandonedCheckout.lineItems.${number}.catalogReference.catalogItemId`
    | `abandonedCheckout.lineItems.${number}.catalogReference.appId`
    | `abandonedCheckout.lineItems.${number}.productName.original`
    | `abandonedCheckout.lineItems.${number}.physicalProperties.shippable`
    | `abandonedCheckout.appliedDiscounts`
    | `abandonedCheckout.appliedDiscounts.${number}.coupon._id`
    | `abandonedCheckout.appliedDiscounts.${number}.coupon.code`
    | `abandonedCheckout.appliedDiscounts.${number}.coupon.name`
    | `abandonedCheckout.appliedDiscounts.${number}.discountRule._id`
    | `abandonedCheckout.appliedDiscounts.${number}.discountRule.name.original`
    | `abandonedCheckout.appliedDiscounts.${number}.discountType`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    abandonedCheckoutId: abandonedCheckoutId,
    activityType: options?.activityType,
  });

  const reqOpts =
    ambassadorWixEcomV1AbandonedCheckout.addAbandonedCheckoutActivity(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'abandonedCheckout.lineItems.image' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          abandonedCheckoutId: '$[0]',
          activityType: '$[1].activityType',
        },
        singleArgumentUnchanged: false,
      },
      ['abandonedCheckoutId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface AddAbandonedCheckoutActivityOptions {
  /** The type of the activity to add */
  activityType?: ActivityTypeWithLiterals;
}

/**
 * Returns site-level aggregated analytics for the site's abandoned checkouts within a date range.
 *
 * A "recoverable" checkout is an abandoned checkout still eligible to be won back; a "recovered"
 * checkout is one the customer later returned to and completed. The site is resolved from the
 * request context, and the date range is interpreted in the site's time zone.
 *
 * Use `metrics` to compute only a subset of the values, and `statuses` to restrict the aggregation
 * to checkouts in specific recovery states.
 * @param startDate - First day to include in the aggregation, inclusive, as a `YYYY-MM-DD` calendar date
 * (e.g. `2022-06-01`) interpreted in the site's time zone.
 * @public
 * @documentationMaturity preview
 * @requiredField options.endDate
 * @requiredField startDate
 * @permissionId ecom:v1:abandoned_checkout:get_abandoned_checkout_analytics
 * @applicableIdentity APP
 * @returns Site-level aggregate over the requested date range. Each field is populated only when its metric was
 * requested in `metrics` (or `metrics` was left empty); metrics that were not requested are left unset.
 * Monetary amounts are decimal strings (e.g. `"1250.90"`) denominated in `currency`.
 * @fqn wix.ecom.abandoned_checkout.api.v1.AbandonedCheckoutService.GetAbandonedCheckoutAnalytics
 */
export async function getAbandonedCheckoutAnalytics(
  startDate: string,
  options?: NonNullablePaths<GetAbandonedCheckoutAnalyticsOptions, `endDate`, 0>
): Promise<
  NonNullablePaths<GetAbandonedCheckoutAnalyticsResponse, `currency`, 0>
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    startDate: startDate,
    endDate: options?.endDate,
    metrics: options?.metrics,
    statuses: options?.statuses,
  });

  const reqOpts =
    ambassadorWixEcomV1AbandonedCheckout.getAbandonedCheckoutAnalytics(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          startDate: '$[0]',
          endDate: '$[1].endDate',
          metrics: '$[1].metrics',
          statuses: '$[1].statuses',
        },
        singleArgumentUnchanged: false,
      },
      ['startDate', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface GetAbandonedCheckoutAnalyticsOptions {
  /**
   * Last day to include in the aggregation, inclusive, as a `YYYY-MM-DD` calendar date
   * (e.g. `2022-07-06`) interpreted in the site's time zone. Must be on or after `start_date`.
   * @format LOCAL_DATE
   * @minLength 10
   */
  endDate: string;
  /**
   * Metrics to compute. Each selected metric populates its matching response field, leaving the
   * rest unset. Leave empty to compute every metric.
   *
   * Metrics are computed only over the checkouts left after `statuses` is applied, so a
   * recovered-only metric (`RECOVERED_VALUE`, `RECOVERED_CHECKOUT_COUNT`) combined with
   * `statuses = [ABANDONED]` returns `0`, and a recoverable-only metric
   * (`RECOVERABLE_VALUE`, `RECOVERABLE_CHECKOUT_COUNT`) combined with `statuses = [RECOVERED]`
   * returns `0`. Leave `statuses` empty to compute each metric over its full population.
   * @maxSize 6
   */
  metrics?: MetricWithLiterals[];
  /**
   * Restricts the aggregation to abandoned checkouts currently in these recovery states
   * (`ABANDONED`, `RECOVERED`). Leave empty to include checkouts regardless of status.
   *
   * This filter scopes the population that every requested metric is computed over. Pairing it
   * with a metric for the opposite state yields `0` (e.g. `statuses = [ABANDONED]` with
   * `RECOVERED_VALUE`); leave empty unless you intend to narrow all metrics to these states.
   * @maxSize 2
   */
  statuses?: StatusWithLiterals[];
}

/**
 * Redirects an abandoned checkout to its checkout page.
 * @param abandonedCheckoutId - ID of the abandoned checkout.
 * @param metasiteId - Metasite ID of the site associated with the abandoned checkout.
 * @public
 * @requiredField abandonedCheckoutId
 * @requiredField metasiteId
 * @fqn wix.ecom.abandoned_checkout.api.v1.AbandonedCheckoutService.RedirectToCheckout
 */
export async function redirectToCheckout(
  abandonedCheckoutId: string,
  metasiteId: string
): Promise<
  NonNullablePaths<
    RawHttpResponse,
    `body` | `headers` | `headers.${number}.key` | `headers.${number}.value`,
    2
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    abandonedCheckoutId: abandonedCheckoutId,
    metasiteId: metasiteId,
  });

  const reqOpts =
    ambassadorWixEcomV1AbandonedCheckout.redirectToCheckout(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          abandonedCheckoutId: '$[0]',
          metasiteId: '$[1]',
        },
        singleArgumentUnchanged: false,
      },
      ['abandonedCheckoutId', 'metasiteId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}
